/**
 * Generic typed-confirm delete dialog used by V2 detail screens.
 *
 * Two-mode behavior:
 *   - confirmText not required → simple Cancel / Delete buttons
 *   - confirmText required → user must type the entity name to enable
 *     the destructive button (saves accidental cascades)
 *
 * Keep this distinct from LessorDeleteDialog: that one carries
 * lessor-specific copy (linked-property warning). Detail screens for
 * properties + rooms use this generic version.
 */
import { useEffect, useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useLanguage } from '@/contexts/LanguageContext'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** What we're deleting — used in the title. */
  entityLabel: string
  /** The specific name shown in the title + (when typed-confirm) what the user types. */
  entityName: string
  /** Body of the dialog, free-form so callers can render lists / counts. */
  description?: React.ReactNode
  /** When true, requires the user to type entityName before Delete enables. */
  requireTypedConfirm?: boolean
  /** Action label, defaults to "Delete". */
  actionLabel?: string
  isDeleting?: boolean
  onConfirm: () => void
}

export function DeleteConfirmDialog({
  open,
  onOpenChange,
  entityLabel,
  entityName,
  description,
  requireTypedConfirm,
  actionLabel,
  isDeleting,
  onConfirm,
}: Props) {
  const { t } = useLanguage()
  const [typed, setTyped] = useState('')
  const matches = typed.trim() === entityName.trim() && typed.length > 0
  const canConfirm = requireTypedConfirm ? matches : true
  const resolvedActionLabel = actionLabel ?? t('admin.action.delete', 'Delete')

  useEffect(() => {
    if (!open) setTyped('')
  }, [open])

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            {t('admin.action.delete', 'Delete')} {entityLabel}?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>
                <strong className="text-foreground">{entityName}</strong> will be
                permanently deleted. This action cannot be undone.
              </p>
              {description}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        {requireTypedConfirm && (
          <div className="space-y-1.5">
            <Label htmlFor="confirm-name" className="text-xs font-normal text-muted-foreground">
              {t('admin.delete.typePrefix', 'Type')}{' '}
              <span className="font-mono text-foreground">{entityName}</span>{' '}
              {t('admin.delete.typeSuffix', 'to confirm')}
            </Label>
            <Input
              id="confirm-name"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              disabled={isDeleting}
            />
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>
            {t('admin.action.cancel', 'Cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={!canConfirm || isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting
              ? t('admin.action.deleting', 'Deleting…')
              : resolvedActionLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
